import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useGame } from "@/context/GameContext";
import { BUSINESSES } from "@/data/gameData";
import { LOGO_OPTIONS, BRAND_COLORS, NAME_IDEAS, defaultBrand } from "@/data/brandData";
import BrandBadge from "./BrandBadge";
import { Sound } from "@/lib/sound";
import { Check, Shuffle } from "lucide-react";
import { toast } from "sonner";

const BrandEditor = ({ businessId, open, onClose }) => {
  const { state, saveBrand } = useGame();
  const biz = BUSINESSES[businessId];
  const [b, setB] = useState(() => ({ ...defaultBrand(biz, state.playerName), ...(state.brands[businessId] || {}) }));
  const set = (patch) => { Sound.tap(); setB((x) => ({ ...x, ...patch })); };
  const ideas = NAME_IDEAS[businessId];

  const save = () => {
    const name = b.name.trim() || defaultBrand(biz, state.playerName).name;
    saveBrand(businessId, { ...b, name });
    Sound.success();
    toast.success(`${name} is official!`);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto rounded-3xl border-4 border-sky-300 bg-white p-5 sm:p-6" data-testid="brand-editor-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl sm:text-3xl font-bold text-slate-900">🏷️ Brand my {biz.name} business</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600 font-medium -mt-2">A great name and logo help customers remember you. It shows on your Hub card, the top bar and your flyers!</p>

        <div className="flex justify-center py-2">
          <BrandBadge brand={b} biz={biz} size="lg" testid="brand-preview" />
        </div>

        <div className="space-y-4">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Business name</div>
            <Input value={b.name} maxLength={28} onChange={(e) => setB((x) => ({ ...x, name: e.target.value }))} className="rounded-xl border-2 border-slate-300 font-bold text-base" data-testid="brand-name-input" />
            <div className="flex flex-wrap gap-1.5 mt-2">
              {ideas.map((n) => (
                <button key={n} type="button" onClick={() => set({ name: n })} data-testid={`brand-idea-${n.replace(/\s+/g, "-").toLowerCase()}`} className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-amber-100 border border-slate-200 text-xs font-heading font-black text-slate-700">
                  💡 {n}
                </button>
              ))}
              <button type="button" onClick={() => set({ name: `${state.playerName}'s ${ideas[Math.floor(Math.random() * ideas.length)]}` })} data-testid="brand-name-random" className="px-2.5 py-1 rounded-lg bg-amber-100 border border-amber-300 text-xs font-heading font-black text-amber-800 inline-flex items-center gap-1">
                <Shuffle className="w-3 h-3" /> Mix it
              </button>
            </div>
          </div>

          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Logo</div>
            <div className="flex flex-wrap gap-1.5">
              <button type="button" onClick={() => set({ logo: "avatar" })} data-testid="brand-logo-avatar" className={`w-11 h-11 rounded-xl border-2 text-[10px] font-black leading-tight ${b.logo === "avatar" ? "bg-amber-300 border-amber-600" : "bg-white border-slate-200 hover:border-amber-300"}`}>
                My face
              </button>
              {LOGO_OPTIONS.map((l) => (
                <button key={l} type="button" onClick={() => set({ logo: l })} data-testid={`brand-logo-${l.codePointAt(0).toString(16)}`} className={`w-11 h-11 rounded-xl border-2 text-2xl ${b.logo === l ? "bg-amber-300 border-amber-600 scale-110" : "bg-white border-slate-200 hover:border-amber-300"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Brand color</div>
            <div className="flex flex-wrap gap-1.5">
              <button type="button" onClick={() => set({ color: null })} data-testid="brand-color-default" className={`px-3 h-9 rounded-full border-4 text-xs font-black ${!b.color ? "border-slate-900" : "border-white shadow"}`} style={{ backgroundColor: biz.primary, color: "#fff" }}>Default</button>
              {BRAND_COLORS.map((c) => (
                <button key={c.id} type="button" onClick={() => set({ color: c.bg })} data-testid={`brand-color-${c.id}`} aria-label={c.name} className={`w-9 h-9 rounded-full border-4 transition-transform hover:scale-110 ${b.color === c.bg ? "border-slate-900 scale-110" : "border-white shadow"}`} style={{ backgroundColor: c.bg }} />
              ))}
            </div>
          </div>

          <Button onClick={save} data-testid="brand-save-btn" className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-heading font-black text-base py-6 rounded-2xl btn-3d border-2 border-emerald-600">
            <Check className="w-5 h-5 mr-1" /> Make it official!
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BrandEditor;
